import { updateTripOnWorker } from "./share-url";
import { stops, type StopChangeDetail } from "./stops";

/** Long enough to coalesce a burst of typing in the edit dialog, short enough to feel live. */
const DEBOUNCE_MS = 2000;

let timer: number | null = null;

/**
 * Once a trip has been shared, its link is live — so every later edit is published to the
 * same id automatically, and phones holding the link pick it up on their next refresh.
 *
 * Nothing here touches the network before the first share: an unshared trip has no `ShareRef`,
 * so `schedulePush` returns immediately.
 *
 * Publishing is best-effort. `localStorage` is the source of truth, so a failed push is not an
 * error the user needs to act on — the next edit, or coming back online, retries it.
 */
export function startShareSync(): void {
  stops.addEventListener("change", (e) => {
    const detail = (e as CustomEvent<StopChangeDetail>).detail;
    // A load isn't an edit — it would push the trip straight back on every page open.
    if (detail.kind === "load") return;
    schedulePush();
  });

  // An edit made offline should land as soon as there's signal again.
  window.addEventListener("online", schedulePush);
}

function schedulePush(): void {
  if (!stops.getShare()) return;
  if (timer !== null) clearTimeout(timer);
  timer = window.setTimeout(() => {
    timer = null;
    void push();
  }, DEBOUNCE_MS);
}

async function push(): Promise<void> {
  const share = stops.getShare();
  if (!share) return;
  try {
    await updateTripOnWorker(share, stops.all(), stops.getTripName() || undefined);
  } catch {
    // Best-effort by design; see the note above.
  }
}
